import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { Plus, Search, RefreshCw, Eraser, Filter, FileText, Navigation, CalendarDays } from 'lucide-react';
import { OsFiltrosPanel, OsFilterState } from '../components/ordemServicoList/OsFiltrosPanel';
import { OsDataGrid } from '../components/ordemServicoList/OsDataGrid';
import { OsExportDropdown } from '../components/ordemServicoList/OsExportDropdown';
import { VisualizarOSModal } from './VisualizarOSModal';
import { CadastroOSModal } from './CadastroOSModal';
import { CriadorRoteiroModal } from '../components/CriadorRoteiroModal';
import { EmitirCteModal } from '../components/fiscal/EmitirCteModal';

const initialFilters: OsFilterState = {
  data_atendimento: '',
  data_atendimento_fim: '',
  pedido_id: '',
  agregado: '',
  status_id: '',
  id: '',
};

export function OrdensServico() {
  const [ordens, setOrdens] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Filtros State
  const [filters, setFilters] = useState<OsFilterState>(initialFilters);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFiltros, setShowFiltros] = useState(false);

  // Modais State
  const [viewOSId, setViewOSId] = useState<number | null>(null);
  const [editOSId, setEditOSId] = useState<number | null>(null);
  const [isCadastroOpen, setIsCadastroOpen] = useState<boolean>(false);
  const [isRoteiroOpen, setIsRoteiroOpen] = useState<boolean>(false);
  const [osParaCte, setOsParaCte] = useState<any | null>(null);

  // Contagem de filtros ativos
  const activeFiltersCount = Object.values(filters).filter((val) => val !== '').length;

  useEffect(() => {
    fetchOrdens();
  }, []);

  const fetchOrdens = async () => {
    try {
      setLoading(true);
      setErrorMsg(null);

      // Busca veículos vinculados ao motorista/agregado filtrado para capturar OSs com motorista pelo veículo
      let matchingVeiculoIds: string[] = [];
      if (filters.agregado && !isNaN(Number(filters.agregado))) {
        const agId = Number(filters.agregado);
        try {
          const { data: vList } = await supabase
            .from('veiculos')
            .select('id')
            .or(`agregado_id.eq.${agId},motorista_id.eq.${agId}`);
          if (vList && vList.length > 0) {
            matchingVeiculoIds = vList.map((v) => String(v.id));
          }
        } catch (vErr) {
          console.warn('Erro ao buscar veículos vinculados ao agregado:', vErr);
        }
      }

      // Função para criar query filtrada para cada página
      const buildQuery = () => {
        let q = supabase
          .from('ordem_de_servico')
          .select('*')
          .order('id', { ascending: false });

        if (filters.data_atendimento) {
          q = q.gte('data_atendimento', filters.data_atendimento);
        }
        if (filters.data_atendimento_fim) {
          q = q.lte('data_atendimento', filters.data_atendimento_fim);
        }
        if (filters.pedido_id && !isNaN(Number(filters.pedido_id))) {
          q = q.eq('pedido_id', Number(filters.pedido_id));
        }
        if (filters.agregado && !isNaN(Number(filters.agregado))) {
          const agId = Number(filters.agregado);
          if (matchingVeiculoIds.length > 0) {
            const veiculosInStr = matchingVeiculoIds.map((id) => `"${id}"`).join(',');
            q = q.or(`agregado.eq.${agId},veiculos_id.in.(${veiculosInStr})`);
          } else {
            q = q.eq('agregado', agId);
          }
        }
        if (filters.status_id && !isNaN(Number(filters.status_id))) {
          q = q.eq('status_id', Number(filters.status_id));
        }
        if (filters.id) {
          const ids = filters.id
            .split(',')
            .map((i) => parseInt(i.trim()))
            .filter((n) => !isNaN(n));
          if (ids.length > 0) {
            q = q.in('id', ids);
          }
        }
        return q;
      };

      let allOSData: any[] = [];
      let from = 0;
      const step = 1000; // Aumentado para 1000 para reduzir pela metade o número de requisições
      
      while (from < 100000) { // Limite de segurança para evitar loop infinito
        let chunk: any[] | null = null;
        let chunkError: any = null;
        let retries = 3;

        while (retries > 0) {
          const res = await buildQuery().range(from, from + step - 1);
          chunk = res.data;
          chunkError = res.error;

          if (!chunkError) break;

          console.warn(`Retry fetching OS chunk at ${from}. Retries left: ${retries - 1}`, chunkError.message);
          retries--;
          if (retries > 0) {
            await new Promise((resolve) => setTimeout(resolve, 1000));
          }
        }

        if (chunkError) {
          console.error("Error fetching OS chunk", chunkError.message, chunkError.details, chunkError.hint);
          setErrorMsg("Erro ao buscar Ordens de Serviço: " + chunkError.message);
          break;
        }

        if (chunk && chunk.length > 0) {
          allOSData = [...allOSData, ...chunk];
          if (chunk.length < step) break;
          from += step;
        } else {
          break;
        }
      }
      const osData = allOSData;

      if (!osData || osData.length === 0) {
        setOrdens([]);
        return;
      }

      // Enriquecimento concorrente dos relacionamentos (Cliente, Status, Motorista, Veículo)
      const pedidoIds = Array.from(new Set(osData.map((os) => os.pedido_id).filter(Boolean)));
      const statusIds = Array.from(new Set(osData.map((os) => os.status_id).filter(Boolean)));
      const veiculoIds = Array.from(new Set(osData.map((os) => os.veiculos_id).filter(Boolean)));

      // 1. Busca paralela de Pedidos, Status e Veículos
      const fetchPedidos = async () => {
        if (pedidoIds.length === 0) return [];
        try {
          const { data } = await supabase.from('pedido').select('*').in('id', pedidoIds);
          return data || [];
        } catch {
          return [];
        }
      };

      const fetchStatus = async () => {
        if (statusIds.length === 0) return [];
        try {
          const { data } = await supabase.from('status').select('*').in('id', statusIds);
          return data || [];
        } catch {
          return [];
        }
      };

      const fetchVeiculos = async () => {
        if (veiculoIds.length === 0) return [];
        try {
          const { data } = await supabase.from('veiculos').select('*').in('id', veiculoIds);
          return data || [];
        } catch {
          return [];
        }
      };

      const [pedidosRes, statusRes, veiculosRes] = await Promise.all([
        fetchPedidos(),
        fetchStatus(),
        fetchVeiculos(),
      ]);

      // Coleta unificada de todos os IDs de pessoas necessários (Clientes dos pedidos, motoristas das OSs e dos veículos)
      const todasPessoasIds = new Set<number>();
      pedidosRes.forEach((p: any) => {
        if (p.pessoa_id) todasPessoasIds.add(Number(p.pessoa_id));
      });
      osData.forEach((os) => {
        const agId = Number(os.agregado);
        if (agId && !isNaN(agId)) todasPessoasIds.add(agId);
      });
      veiculosRes.forEach((v: any) => {
        if (v.motorista_id) todasPessoasIds.add(Number(v.motorista_id));
        if (v.agregado_id) todasPessoasIds.add(Number(v.agregado_id));
      });

      // 2. Consulta unificada em lote de nomes de pessoas (1 única requisição)
      let pessoaNomeMap = new Map<string, string>();
      if (todasPessoasIds.size > 0) {
        try {
          const { data: pessoasData } = await supabase
            .from('pessoa')
            .select('id, nome')
            .in('id', Array.from(todasPessoasIds));
          if (pessoasData) {
            pessoasData.forEach((p) => pessoaNomeMap.set(String(p.id), p.nome));
          }
        } catch (e) {
          console.warn('Erro ao carregar pessoas vinculadas às OSs:', e);
        }
      }

      // Mapeamento de Clientes via Pedido
      let clienteMap = new Map<string, string>();
      pedidosRes.forEach((p: any) => {
        if (p.pessoa_id && pessoaNomeMap.has(String(p.pessoa_id))) {
          clienteMap.set(String(p.id), pessoaNomeMap.get(String(p.pessoa_id))!);
        }
      });

      // Mapeamento de Status
      let statusMap = new Map<string, { status: string; cor: string }>();
      statusRes.forEach((s: any) => {
        statusMap.set(String(s.id), { status: s.status, cor: s.cor });
      });

      // Mapeamento de Veículos & Motoristas
      let veiculoMotoristaMap = new Map<string, string>();
      let veiculoNomeMap = new Map<string, string>();
      veiculosRes.forEach((v: any) => {
        veiculoNomeMap.set(String(v.id), `Veículo (${v.placa || 'Sem placa'})`);
        const motId = v.motorista_id || v.agregado_id;
        if (motId && pessoaNomeMap.has(String(motId))) {
          veiculoMotoristaMap.set(String(v.id), pessoaNomeMap.get(String(motId))!);
        }
      });

      // Monta objeto final formatado com preservação histórica de motorista
      const formatted = osData.map((os) => {
        // PRIORIDADE 1: Motorista gravado na própria Ordem de Serviço (Histórico Imutável)
        const motIdOS = os.agregado ? String(os.agregado) : null;
        let motoristaFinal = '-';
        if (motIdOS && pessoaNomeMap.has(motIdOS)) {
          motoristaFinal = pessoaNomeMap.get(motIdOS)!;
        } else if (os.veiculos_id && veiculoMotoristaMap.has(String(os.veiculos_id))) {
          motoristaFinal = veiculoMotoristaMap.get(String(os.veiculos_id))!;
        } else if (os.veiculos_id) {
          motoristaFinal = `Sem motorista (ID: ${os.veiculos_id})`;
        }

        const rawTotal = Number(os.total_os || 0);
        const valorServi = Number(os.valor_unit_servi || 0);
        const valorAjud = Number(os.valor_unit_ajud || 0);
        const qtdAjud = Number(os.quantidade_ajudante || 0);
        const adicional = Number(os.adicional || 0);
        
        const totalCalculado = rawTotal > 0
          ? rawTotal
          : Number((valorServi + (valorAjud * qtdAjud) + adicional).toFixed(2));

        const repasse = Number(os.repasse || 0);
        const despesa = Number(os.valor_despesa || 0);
        const ajudanteRepasse = Number(os.ajudante_repasse || 0);
        const repassesTotais = Number(os.valor_total_repasses || (repasse + despesa + ajudanteRepasse));

        const valorLiquidoCalculado = (os.valor_liquido !== undefined && os.valor_liquido !== null && Number(os.valor_liquido) !== 0)
          ? Number(os.valor_liquido)
          : Number((totalCalculado - repassesTotais).toFixed(2));

        return {
          ...os,
          total_os: totalCalculado,
          valor_liquido: valorLiquidoCalculado,
          cliente_nome: clienteMap.get(String(os.pedido_id)) || `Cliente/Pedido #${os.pedido_id || 'N/A'}`,
          status_nome: statusMap.get(String(os.status_id))?.status || `Status #${os.status_id || 'N/A'}`,
          status_cor: statusMap.get(String(os.status_id))?.cor || '#64748b',
          motorista_nome: motoristaFinal,
          veiculo_nome: os.veiculos_id ? veiculoNomeMap.get(String(os.veiculos_id)) || os.placa || `V_ID:${os.veiculos_id}` : os.placa || 'NO_ID',
        };
      });

      let finalFormatted = formatted;
      if (filters.agregado && !isNaN(Number(filters.agregado))) {
        const agId = Number(filters.agregado);
        finalFormatted = formatted.filter((os) => {
          const motIdOS = os.agregado ? Number(os.agregado) : null;
          if (motIdOS === agId) return true;
          if (os.veiculos_id && matchingVeiculoIds.includes(String(os.veiculos_id))) return true;
          return false;
        });
      }

      setOrdens(finalFormatted);
    } catch (err: any) {
      console.error('Erro detalhado ao buscar ordens de serviço:', err);
      const msg = err?.message || err?.error_description || JSON.stringify(err);
      setErrorMsg(`Falha ao carregar as ordens de serviço do banco de dados: ${msg}`);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenNovaOS = () => {
    setEditOSId(null);
    setIsCadastroOpen(true);
  };

  const handleOpenEditarOS = (id: number) => {
    setEditOSId(id);
    setIsCadastroOpen(true);
  };

  const handleClearFilters = () => {
    setFilters(initialFilters);
    setSearchTerm('');
    setTimeout(() => {
      fetchOrdens();
    }, 50);
  };

  // Filtro secundário por busca rápida na barra
  const filteredOrdens = ordens.filter((os) => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      os.id.toString().includes(term) ||
      (os.descricao && os.descricao.toLowerCase().includes(term)) ||
      (os.cliente_nome && os.cliente_nome.toLowerCase().includes(term)) ||
      (os.retirada && os.retirada.toLowerCase().includes(term)) ||
      (os.entrega && os.entrega.toLowerCase().includes(term))
    );
  });

  return (
    <div className="space-y-4">
      {/* Cabeçalho da Página e Botões Globais */}
      <div className="sm:flex sm:items-center sm:justify-between bg-white p-4 sm:p-5 rounded-2xl border border-slate-200 shadow-xs">
        <div>
          <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <FileText className="w-6 h-6 text-red-600" />
            Ordens de Serviço
          </h1>
          <p className="mt-0.5 text-xs text-slate-500">
            Gerenciamento completo das Ordens de Serviço, repasses, ajudantes e medições
          </p>
        </div>

        {/* Barra de Ações Rápidas (Busca, Cadastrar, Refresh, Limpar, Filtros, Exportar) */}
        <div className="mt-4 sm:mt-0 flex flex-wrap items-center gap-2">
          <div className="relative w-full sm:w-64">
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
              <Search className="h-3.5 w-3.5 text-slate-400" />
            </div>
            <input
              type="text"
              className="block w-full rounded-lg border-slate-300 pl-9 pr-3 py-1.5 border outline-none text-xs text-slate-800 focus:border-red-500 focus:ring-1 focus:ring-red-500"
              placeholder="Busca rápida..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <button
            type="button"
            onClick={fetchOrdens}
            className="px-3 py-1.5 bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 rounded-lg text-xs font-bold transition-colors flex items-center gap-1.5 shadow-2xs"
            title="Atualizar lista"
          >
            <RefreshCw className={`w-3.5 h-3.5 text-sky-600 ${loading ? 'animate-spin' : ''}`} />
            Atualizar
          </button>

          <button
            type="button"
            onClick={handleClearFilters}
            className="px-3 py-1.5 bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 rounded-lg text-xs font-bold transition-colors flex items-center gap-1.5 shadow-2xs"
            title="Limpar todos os filtros"
          >
            <Eraser className="w-3.5 h-3.5 text-rose-600" />
            Limpar Filtros
          </button>

          <button
            type="button"
            onClick={() => setShowFiltros(!showFiltros)}
            className={`px-3 py-1.5 border rounded-lg text-xs font-bold transition-colors flex items-center gap-1.5 relative shadow-2xs ${
              showFiltros || activeFiltersCount > 0
                ? 'bg-red-50 border-red-300 text-red-700'
                : 'bg-white border-slate-300 text-slate-700 hover:bg-slate-50'
            }`}
          >
            <Filter className="w-3.5 h-3.5" />
            Filtros
            {activeFiltersCount > 0 && (
              <span className="ml-1 px-1.5 py-0.2 bg-red-600 text-white rounded-full text-3xs font-extrabold">
                {activeFiltersCount}
              </span>
            )}
          </button>

          <OsExportDropdown ordens={filteredOrdens} />

          <Link
            to="/agenda"
            className="px-3.5 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded-lg text-xs font-bold transition-colors flex items-center gap-1.5 shadow-sm"
            title="Abrir Agenda e Planejador de Fretes"
          >
            <CalendarDays className="w-3.5 h-3.5" />
            Agenda & Planner
          </Link>

          <button
            type="button"
            onClick={() => setIsRoteiroOpen(true)}
            className="px-3.5 py-1.5 bg-slate-900 hover:bg-slate-800 text-white rounded-lg text-xs font-bold transition-colors flex items-center gap-1.5 shadow-sm border border-slate-700"
            title="Programar rota e sequenciamento de paradas para motoristas"
          >
            <Navigation className="w-3.5 h-3.5 text-red-400" />
            Roteirizar Paradas
          </button>

          <button
            type="button"
            onClick={handleOpenNovaOS}
            className="px-4 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-bold transition-colors flex items-center gap-1.5 shadow-sm"
          >
            <Plus className="w-4 h-4" />
            Cadastrar O.S
          </button>
        </div>
      </div>

      {/* Painel Retrátil de Filtros Avançados */}
      {showFiltros && (
        <OsFiltrosPanel
          filters={filters}
          setFilters={setFilters}
          onSearch={fetchOrdens}
          onClear={handleClearFilters}
          onClose={() => setShowFiltros(false)}
        />
      )}

      {/* Tabela de Dados Completa (Datagrid) */}
      {errorMsg && (
        <div className="p-4 bg-red-50 border border-red-200 text-red-800 rounded-xl text-xs font-bold">
          {errorMsg}
        </div>
      )}

      {/* Tabela de Dados Completa (Datagrid) */}
      <OsDataGrid
        ordens={filteredOrdens}
        loading={loading}
        onView={(id) => setViewOSId(id)}
        onEdit={(id) => handleOpenEditarOS(id)}
        onPrint={(id) => setViewOSId(id)}
        onEmitCte={(os) => setOsParaCte(os)}
      />

      {/* Modal de Emissão de CT-e vinculado à OS */}
      <EmitirCteModal
        isOpen={!!osParaCte}
        initialOs={osParaCte}
        onClose={() => setOsParaCte(null)}
        onSuccess={() => {
          setOsParaCte(null);
          fetchOrdens();
        }}
      />

      {/* Modal de Visualização O.S */}
      <VisualizarOSModal
        osId={viewOSId}
        onClose={() => setViewOSId(null)}
      />

      {/* Modal de Cadastro/Edição O.S */}
      <CadastroOSModal
        osId={editOSId}
        isOpen={isCadastroOpen}
        onClose={() => setIsCadastroOpen(false)}
        onSaved={() => {
          setIsCadastroOpen(false);
          fetchOrdens();
        }}
      />

      {/* Modal de Roteirização e Programação de Rotas */}
      <CriadorRoteiroModal
        isOpen={isRoteiroOpen}
        onClose={() => setIsRoteiroOpen(false)}
        onRoteiroSalvo={() => {
          fetchOrdens();
        }}
      />
    </div>
  );
}
